import type {
  ActionDecision,
  AvailabilitySlot,
  CancellationPreview,
  CreditEffect,
  PaymentSummary,
  ReschedulePreview,
} from "./api.js";

/**
 * How the booking system's decisions are put in front of a person.
 *
 * Every function here turns something FastAPI decided into something readable,
 * and none of them decides anything. That division is the point: `allowed` is
 * already true or false when it arrives, and this file's only job is to say why
 * in a sentence somebody can act on.
 *
 * **`code` is what we branch on; `message` is the fallback.** The contract
 * promises the codes are stable and says outright that the prose is safe
 * default copy rather than something to match on. So where the school has its
 * own words for a situation they live in `WORDING` below, keyed by code, and
 * anything without an entry falls through to whatever FastAPI sent — which is
 * always a real sentence, never a stack trace.
 */

/**
 * The school's own words for the reasons a family will actually meet.
 *
 * Deliberately short of exhaustive. A code nobody has written copy for shows
 * FastAPI's message, which is fine; inventing a phrase for every code would
 * mean maintaining a second glossary that goes stale silently.
 */
const WORDING: Record<string, string> = {
  cancellation_notice_period: "Lessons can only be cancelled more than 24 hours ahead.",
  reschedule_notice_period: "This lesson is now too close to move.",
  credit_not_available: "There are no lesson credits left to use for this.",
  credit_unavailable: "No eligible lesson credit is available for this tutor and lesson. Check your credits in the Hub.",
  credit_depleted: "There are no lesson credits left in this block.",
  credit_being_set_up: "These lesson credits are still being set up. The school will confirm when they can be used.",
  credit_claim_pending: "This purchase needs its student and Hub access confirmed before its lesson credits can be used. Please contact the school.",
  credit_migration_hold: "These older lesson credits are being checked by the school. Please contact the office before booking with them.",
  credit_expired: "That block of lessons has expired.",
  credit_scope_mismatch: "Those credits are for a different tutor or a different lesson.",
  slot_unavailable: "That time has just been taken.",
  shared_appointment: "This lesson is shared with somebody else, so your tutor has to change it.",
  booking_state_changed: "This lesson has changed since the page was loaded. Reload and try again.",
};

export function reasonText(code: string, message: string): string {
  return WORDING[code] ?? message;
}

/**
 * Why an action is not available, or nothing at all when it is.
 *
 * Reasons are shown for a refusal and swallowed for a permission: "you may
 * cancel this because the notice period is satisfied" is noise, and the same
 * list under a working button reads as a warning.
 */
export function Reasons({ decision }: { decision: ActionDecision | undefined }) {
  if (!decision || decision.allowed || decision.reasons.length === 0) return null;
  return (
    <ul className="small muted reasons">
      {decision.reasons.map((reason) => (
        <li key={reason.code}>{reasonText(reason.code, reason.message)}</li>
      ))}
    </ul>
  );
}

export type CreditDescriptionMode = "preview" | "confirmed";

/**
 * What cancelling or moving does to the family's credits, in plain words.
 *
 * Preview and confirmed copy deliberately share this one vocabulary owner. The
 * caller chooses tense; it does not reimplement the credit-effect switch.
 */
export function describeCredit(
  credit: CreditEffect | undefined,
  mode: CreditDescriptionMode = "preview",
): string {
  const confirmed = mode === "confirmed";
  if (!credit) return confirmed ? "No lesson credits changed." : "No change to lesson credits.";
  const plural = credit.credits === 1 ? "credit" : "credits";
  switch (credit.effect) {
    case "grant":
      return confirmed
        ? `${credit.credits} rebooking ${plural} was added to the account.`
        : `${credit.credits} rebooking ${plural} will be added to your account.`;
    case "restore":
      return confirmed
        ? `${credit.credits} lesson ${plural} returned to the account.`
        : `${credit.credits} lesson ${plural} would go back onto the account.`;
    case "consume":
      return confirmed
        ? `${credit.credits} lesson ${plural} ${credit.credits === 1 ? "was" : "were"} used.`
        : `${credit.credits} lesson ${plural} would be used.`;
    case "forfeit":
      // Said outright rather than softened. Somebody about to lose a lesson they
      // paid for should read that before pressing, not afterwards.
      return confirmed
        ? `${credit.credits} lesson ${plural} ${credit.credits === 1 ? "was" : "were"} not returned because of the notice given.`
        : `${credit.credits} lesson ${plural} would be lost, because of the notice given.`;
    case "unchanged":
      return confirmed ? "Lesson credits stayed as they were." : "Lesson credits stay as they are.";
    default:
      return confirmed ? "No lesson credits changed." : "No lesson credits are involved.";
  }
}

/** What would happen about money. The manual-review case is the one that matters. */
export function describePaymentEffect(effect: string): string {
  switch (effect) {
    case "none":
      return "There is no change to this lesson’s payment. Any lesson-credit outcome is shown separately.";
    case "amount_remains_due":
      return "The amount owed for this lesson still stands.";
    case "waive_pending":
      return "Nothing will be owed for this lesson.";
    case "manual_refund_review":
      // No automatic refund exists, and promising one here would be a lie the
      // school then has to walk back.
      return "This lesson has been paid for, so a refund will be looked at by the office rather than made automatically.";
    case "retain_as_rebooking_credit":
      // The normal outcome for a paid lesson cancelled in time: value kept as
      // credit. Never phrased as a refund the office might consider.
      return "Your existing payment will be kept as lesson credit. No refund will be made.";
    case "retain_as_cancellation_charge":
      return "Your payment is kept under the cancellation policy. No rebooking credit will be issued.";
    case "requires_review":
      return "The office will check this lesson's payment and confirm what is owed. Nothing is added or refunded automatically.";
    default:
      return "The payment outcome is not available. Check with the office before assuming a waiver, refund or credit.";
  }
}

/**
 * The consequence a family must see before a late cancellation.
 *
 * `notice_satisfied` and the financial effects are answers from the preview;
 * this helper only translates those answers and never decides whether the
 * cancellation is permitted.
 */
export function lateCancellationMessage(preview: CancellationPreview): string | null {
  if (preview.notice_satisfied) return null;
  if (!preview.cancellation.allowed) {
    return "Late cancellation is not available for this lesson under the current policy.";
  }
  if (preview.credit.effect === "forfeit") {
    return "You can still cancel this lesson, but the lesson credit will not be returned.";
  }
  switch (preview.payment_effect) {
    case "retain_as_cancellation_charge":
      return "You can still cancel this lesson, but no rebooking credit will be issued because it is inside the cancellation notice period.";
    case "requires_review":
      return "You can still cancel this lesson. The office will check its payment and confirm what is owed.";
    case "amount_remains_due":
      return "You can still cancel this lesson, but the lesson fee remains payable under the cancellation policy.";
    case "manual_refund_review":
      return "You can still cancel this lesson, but any refund will need to be reviewed by the office under the cancellation policy.";
    case "waive_pending":
      return "You can still cancel this lesson. The pending payment will be handled under the cancellation policy.";
    default:
      return "You can still cancel this lesson, but the financial effect follows the cancellation policy.";
  }
}

/** How a lesson was, or will be, paid for. */
export function describePayment(payment: PaymentSummary | undefined): string {
  if (!payment) return "";
  const method: Record<string, string> = {
    stripe: "Card",
    cash: "Cash",
    card_terminal: "Card machine",
    bank_transfer: "Bank transfer",
    coupon: "Lesson block",
    lesson_credit: "Lesson credit",
    on_site: "Paid on the day",
    complimentary: "No charge",
    paid_cancellation: "Paid cancellation credit",
  };
  const state: Record<string, string> = {
    not_required: "nothing to pay",
    pending: "not yet paid",
    partially_paid: "part paid",
    paid: "paid",
    failed: "payment failed",
    partially_refunded: "partly refunded",
    refunded: "refunded",
  };
  const name = method[payment.method] ?? "Payment";
  const status = state[payment.state] ?? "unknown";
  return `${name} — ${status}`;
}

/**
 * Which decision governs which action.
 *
 * Two of these look interchangeable and are not, and conflating them is a
 * defect that reached acceptance: **`slot.booking` is about starting a new
 * credit-funded booking, and says nothing about moving an existing lesson.**
 * Availability returns it alongside every free time, so it is the nearest
 * decision to hand when a slot is being rendered — and reading it in the move
 * flow disabled every replacement time for any family without a credit, which
 * is most of them. A move consumes no new credit; it is the *reschedule
 * preview* that decides, and it is a separate call for that reason.
 *
 * Written as named predicates rather than inline conditions so the distinction
 * is stated once, tested, and hard to collapse again by accident.
 */

/**
 * Whether a returned free time may be chosen as the new time for an existing
 * lesson.
 *
 * Every one of them. Availability has already answered the only question that
 * matters — the time is free for this tutor and this service — and what a move
 * would cost is the reschedule preview's answer, asked once a time is picked.
 * A slot refused for `no_eligible_credit` is still a perfectly good time to
 * move a lesson to.
 */
export function selectableForMove(_slot: AvailabilitySlot): boolean {
  return true;
}

/** Whether the move may actually be made. The preview, and nothing else. */
export function moveAllowed(preview: ReschedulePreview | null): boolean {
  return preview?.reschedule.allowed === true;
}

/**
 * Whether a slot may be booked as a *new* lesson against a credit.
 *
 * Unchanged, and deliberately stricter: a new booking does spend a credit, so
 * both the decision and the count have to be there. FastAPI knows a block can
 * be in date and still locked to another tutor, which is why the count alone
 * is not enough.
 */
export function bookableWithCredit(slot: AvailabilitySlot): boolean {
  return slot.booking.allowed && slot.eligible_credit_count > 0;
}

/**
 * What somebody asked the booking system to do, in the family's words.
 *
 * The actor is folded into the sentence rather than shown beside it. "Cancelled
 * by the school" and "Cancelled" are different facts to a parent, and a column
 * headed `actor_kind` containing the word `owner` is not how anybody would say
 * it. A tutor and an owner are both "the school" here — which of the two it was
 * is an internal distinction and belongs on the staff side.
 */
export function operationTitle(type: string, actorKind: string): string {
  const school = actorKind === "tutor" || actorKind === "owner";
  const words: Record<string, string> = {
    credit_booking: "Lesson booked with a credit",
    on_site_request: "Asked to pay on the day",
    on_site_approval: "Request confirmed by your tutor",
    on_site_rejection: "Request turned down",
    payment_confirmation: "Payment recorded",
    reschedule: school ? "Lesson moved by the school" : "Lesson moved",
    cancellation: school ? "Lesson cancelled by the school" : "Lesson cancelled",
  };
  return words[type] ?? "Other booking activity";
}

/**
 * How a request ended, in family-facing words.
 *
 * The operation type matters for a completed request: a completed cancellation
 * is "Cancelled", a completed rejection is "Declined", and a completed payment
 * confirmation only records a state. Unknown server states are never prettified
 * into something that looks meaningful to a family.
 */
export function operationOutcome(
  status: string,
  operationType?: string,
): { label: string; warn: boolean } {
  switch (status) {
    case "completed":
      if (operationType === "on_site_rejection") {
        return { label: "Declined", warn: false };
      }
      if (operationType === "cancellation") {
        return { label: "Cancelled", warn: false };
      }
      if (operationType === "payment_confirmation") {
        return { label: "Recorded", warn: false };
      }
      return { label: "Confirmed", warn: false };
    case "received":
    case "upstream_completed":
      if (operationType === "on_site_request") {
        return { label: "Awaiting tutor confirmation", warn: false };
      }
      return { label: "Pending", warn: false };
    case "requires_reconciliation":
      return { label: "Needs school review", warn: true };
    case "failed":
      return { label: "Could not be completed", warn: true };
    default:
      return { label: "Status unavailable", warn: true };
  }
}

/** A short badge for the state of a past lesson. */
export function outcomeLabel(outcome: string, bookingStatus: string): string {
  const words: Record<string, string> = {
    completed: "Taught",
    completed_unpaid: "Taught, unpaid",
    no_show: "Missed",
    student_cancelled_with_notice: "Cancelled",
    student_cancelled_late: "Cancelled late",
    tutor_cancelled: "Cancelled by tutor",
    scheduled: "Booked",
    rescheduled: "Moved",
  };
  return words[outcome] ?? words[bookingStatus] ?? bookingStatus;
}
